/**
 * Admin › Configurações › **Gastos de IA** (D639, Digital Central phase 4, doc 25 section 11 "AI spend panel") - the pure view model behind
 * `AiSpendScreen`: what the Central's AI cost this month by purpose, model, country and channel, the cost per conversation Vello resolved, the
 * end-of-month forecast at the current pace, and every budget judged against its alerts (80 and 100 percent by default).
 *
 * The server does the arithmetic (`/v1/ai/spend`: exact integer micros and minor units); this module only shapes it for a person - amounts as
 * money (a call costs fractions of a cent, so a small amount shows up to four decimals instead of a misleading 0.00), shares, tones, the months
 * to pick from, and what the signed-in operator may do. No country, currency or provider literal.
 */
import { grantsAllow, type KnownGrants } from './console-access.js';
import type { AiSpendBudgetView, AiSpendFailureView, AiSpendGroupView } from '@vello/api-client';

export function aiSpendAuthority(grants: KnownGrants): { readonly mayView: boolean; readonly mayManage: boolean } {
  if (grants === undefined) return { mayView: false, mayManage: false };
  return {
    mayView: grantsAllow(grants, { permission: 'ai_spend:view', target: 'PLATFORM' }),
    mayManage: grantsAllow(grants, { permission: 'ai_spend:manage', target: 'PLATFORM' }),
  };
}

const ISO4217 = /^[A-Z]{3}$/;

/** Digits of a bigint-like decimal string, or undefined. */
const bigOf = (text: string): bigint | undefined => (/^-?\d+$/.test(text) ? BigInt(text) : undefined);

/** The decimal text of a MICROS amount (millionths of the major unit), exactly: `1234` -> `0.001234`. */
export function microsDecimal(micros: string): string | undefined {
  const n = bigOf(micros);
  if (n === undefined) return undefined;
  const negative = n < 0n;
  const digits = (negative ? -n : n).toString().padStart(7, '0');
  return `${negative ? '-' : ''}${digits.slice(0, digits.length - 6)}.${digits.slice(digits.length - 6)}`;
}

/**
 * A MICROS amount as money in `locale`: two decimals normally, and up to four while the amount is under one major unit (the cost of a
 * conversation is often a fraction of a cent: showing 0.00 would say it was free). An unreadable amount is shown as the dash.
 */
export function microsMoney(micros: string, currency: string, locale: string): string {
  const decimal = microsDecimal(micros);
  if (decimal === undefined || !ISO4217.test(currency)) return '—';
  const small = Math.abs(Number(decimal)) < 1;
  const nf = new Intl.NumberFormat(locale, { style: 'currency', currency, minimumFractionDigits: small ? 4 : undefined, maximumFractionDigits: small ? 4 : undefined });
  // Exact: Intl parses a decimal STRING as an exact value (ES2023); the lib types predate that.
  return (nf.format as unknown as (value: string) => string)(decimal);
}

/** An amount of MINOR units of a currency as money in `locale`. */
export function minorMoney(minor: string, currency: string, locale: string): string {
  const n = bigOf(minor);
  if (n === undefined || !ISO4217.test(currency)) return '—';
  const exponent = new Intl.NumberFormat('en', { style: 'currency', currency }).resolvedOptions().maximumFractionDigits ?? 2;
  const negative = n < 0n;
  const digits = (negative ? -n : n).toString().padStart(exponent + 1, '0');
  const decimal = `${negative ? '-' : ''}${exponent === 0 ? digits : `${digits.slice(0, digits.length - exponent)}.${digits.slice(digits.length - exponent)}`}`;
  return (new Intl.NumberFormat(locale, { style: 'currency', currency }).format as unknown as (value: string) => string)(decimal);
}

/** Basis points (10 000 = 100 %) as a percentage text, one decimal under 10 %. */
export function percentOfBp(bp: number | undefined): string {
  if (bp === undefined) return '—';
  return bp < 1000 ? `${(bp / 100).toFixed(1)}%` : `${Math.round(bp / 100)}%`;
}

export type SpendTone = 'neutral' | 'success' | 'warning' | 'danger';

/** The tone of a budget's state: within budget, close to the limit, over it, or one that cannot be judged. */
export function budgetTone(state: string): SpendTone {
  switch (state) {
    case 'OK': return 'success';
    case 'ALERT': return 'warning';
    case 'EXCEEDED': case 'INVALID': return 'danger';
    case 'NOT_COMPARABLE': return 'warning';
    default: return 'neutral';
  }
}

/** The share of `group` in the total of `groups` of the same currency, in basis points (0 when the total is zero). */
export function shareBp(group: AiSpendGroupView, groups: readonly AiSpendGroupView[]): number {
  const total = groups.filter((g) => g.currency === group.currency).reduce((n, g) => n + BigInt(g.costMicros), 0n);
  if (total === 0n) return 0;
  return Number((BigInt(group.costMicros) * 10_000n) / total);
}

/** The cost of one call on average, in micros (0 for no calls). */
export function perCallMicros(group: AiSpendGroupView): string {
  return group.calls === 0 ? '0' : ((BigInt(group.costMicros) + BigInt(Math.floor(group.calls / 2))) / BigInt(group.calls)).toString();
}

/** The months a person can look at: the current UTC month and the eleven before it, newest first (`YYYY-MM`). */
export function monthChoices(nowIso: string, count = 12): readonly string[] {
  const d = new Date(nowIso);
  let year = d.getUTCFullYear();
  let month = d.getUTCMonth();
  const out: string[] = [];
  for (let i = 0; i < count; i++) {
    out.push(`${year}-${String(month + 1).padStart(2, '0')}`);
    month -= 1;
    if (month < 0) {
      month = 11;
      year -= 1;
    }
  }
  return out;
}

/** The budgets that need a person's attention first: exceeded, then alert, then the ones that cannot be judged, then the rest; the platform's before a country's. */
export function budgetRows(budgets: readonly AiSpendBudgetView[]): readonly AiSpendBudgetView[] {
  const rank = (b: AiSpendBudgetView): number => (b.state === 'EXCEEDED' ? 0 : b.state === 'ALERT' ? 1 : b.state === 'NOT_COMPARABLE' || b.state === 'INVALID' ? 2 : b.state === 'OK' ? 3 : 4);
  return [...budgets].sort((a, b) => rank(a) - rank(b) || (a.scope === b.scope ? (a.country ?? '').localeCompare(b.country ?? '') : a.scope === 'platform' ? -1 : 1));
}

/** The currency pairs a screen can offer for a missing exchange rate (`USD->BRL` -> `{from: 'USD', to: 'BRL'}`). */
export function missingRatePairs(budgets: readonly AiSpendBudgetView[]): readonly { readonly from: string; readonly to: string }[] {
  const seen = new Set<string>();
  const out: { from: string; to: string }[] = [];
  for (const b of budgets) {
    for (const pair of b.missingRates ?? []) {
      const [from, to] = pair.split('->');
      if (from !== undefined && to !== undefined && ISO4217.test(from) && ISO4217.test(to) && !seen.has(pair)) {
        seen.add(pair);
        out.push({ from, to });
      }
    }
  }
  return out;
}

/** True while `text` is a rate the server will accept: a positive decimal with at most 6 decimals. */
export const isRateText = (text: string): boolean => /^(?:\d{1,9})(?:\.\d{1,6})?$/.test(text.trim()) && Number(text) > 0;

/**
 * D851: the account-level cause an operator must fix OUTSIDE the console, if the month's failures show one - the provider refused for lack of credit
 * (`credits`) or refused the key (`key`). Every model fails the same way then, so the assistant hands every conversation to people until it is fixed.
 * The latest such failure wins (a key fixed after the credit ran out shows the credit), with its instant so a person can tell a past episode from a
 * current one. Undefined = no such cause (a timeout or an outage passes on its own).
 */
export function providerAccountIssue(failures: readonly AiSpendFailureView[] | undefined): { readonly kind: 'credits' | 'key'; readonly lastAt: string } | undefined {
  let found: { kind: 'credits' | 'key'; lastAt: string } | undefined;
  for (const f of failures ?? []) {
    const kind = f.reasonCode === 'AI_PROVIDER_REJECTED_KEY' ? 'key' : f.reasonCode === 'AI_PROVIDER_CREDITS_EXHAUSTED' ? 'credits' : undefined;
    if (kind !== undefined && (found === undefined || f.lastAt > found.lastAt)) found = { kind, lastAt: f.lastAt };
  }
  return found;
}
